/**
 * CasesRemediation - Cases & Remediation Management
 * 
 * @description Manages incidents, complaints, investigations, and remediation tracking
 * for CSDDD compliance. Part of the Operations pillar.
 * 
 * Features:
 * - Case intake and triage
 * - Investigation workflow
 * - Remediation tracking
 * - Evidence linking
 * - Audit trail
 * 
 * @route /cases
 * @pillar OPERATIONS
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  Search,
  Plus,
  Filter,
  Users,
  Calendar,
  ChevronRight,
  MessageSquare,
  Link2,
  Shield,
  TrendingUp,
  ArrowUpRight,
  MoreHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DefensibilityFlywheel from '../components/DefensibilityFlywheel';

/**
 * Case status configuration with colors and labels
 */
const CASE_STATUSES = {
  intake: { label: 'Intake', color: 'bg-gray-500/20 text-gray-400', icon: FileText },
  triage: { label: 'Triage', color: 'bg-blue-500/20 text-blue-400', icon: Filter },
  investigation: { label: 'Investigation', color: 'bg-amber-500/20 text-amber-400', icon: Search },
  remediation: { label: 'Remediation', color: 'bg-purple-500/20 text-purple-400', icon: Shield },
  verification: { label: 'Verification', color: 'bg-cyan-500/20 text-cyan-400', icon: CheckCircle2 },
  closed: { label: 'Closed', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
};

/**
 * Case priority configuration
 */
const CASE_PRIORITIES = {
  critical: { label: 'Critical', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  medium: { label: 'Medium', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  low: { label: 'Low', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

/**
 * Case type configuration
 */
const CASE_TYPES = {
  grievance: { label: 'Grievance', icon: MessageSquare },
  audit_finding: { label: 'Audit Finding', icon: Search },
  incident: { label: 'Incident', icon: AlertTriangle },
  allegation: { label: 'Allegation', icon: FileText },
  whistleblower: { label: 'Whistleblower', icon: Users },
};

/**
 * Mock case data - would come from API in production
 */
const MOCK_CASES = [
  {
    case_id: 'case_001',
    title: 'Overtime violations at Supplier XYZ',
    type: 'audit_finding',
    status: 'remediation',
    priority: 'high',
    supplier_id: 'sup_001',
    supplier_name: 'Global Textiles Co',
    impact_type: 'human_rights',
    description: 'Third-party audit identified systematic overtime violations exceeding legal limits',
    reported_at: '2025-01-15T10:00:00Z',
    due_date: '2025-03-15',
    assignee: 'Maria Garcia',
    evidence_count: 4,
    investigation_findings: 'Confirmed violations in 3 facilities',
    remediation_plan: 'Implement time tracking system, manager training',
    remediation_progress: 65,
  },
  {
    case_id: 'case_002',
    title: 'Environmental discharge complaint',
    type: 'grievance',
    status: 'investigation',
    priority: 'critical',
    supplier_id: 'sup_002',
    supplier_name: 'Chemical Processing Ltd',
    impact_type: 'environment',
    description: 'Community complaint about wastewater discharge affecting local water sources',
    reported_at: '2025-02-01T14:30:00Z',
    due_date: '2025-02-28',
    assignee: 'John Smith',
    evidence_count: 2,
    investigation_findings: null,
    remediation_plan: null,
    remediation_progress: 0,
  },
  {
    case_id: 'case_003',
    title: 'Child labor allegation - Tier 2 supplier',
    type: 'allegation',
    status: 'triage',
    priority: 'critical',
    supplier_id: 'sup_003',
    supplier_name: 'Regional Components Mfg',
    impact_type: 'human_rights',
    description: 'NGO report alleging underage workers at component manufacturing facility',
    reported_at: '2025-02-10T09:00:00Z',
    due_date: '2025-02-20',
    assignee: null,
    evidence_count: 1,
    investigation_findings: null,
    remediation_plan: null,
    remediation_progress: 0,
  },
  {
    case_id: 'case_004',
    title: 'Safety incident - warehouse collapse',
    type: 'incident',
    status: 'verification',
    priority: 'high',
    supplier_id: 'sup_004',
    supplier_name: 'Logistics Partners Inc',
    impact_type: 'human_rights',
    description: 'Partial warehouse collapse during loading operations, 2 injuries reported',
    reported_at: '2024-12-20T16:00:00Z',
    due_date: '2025-02-01',
    assignee: 'Sarah Chen',
    evidence_count: 8,
    investigation_findings: 'Structural deficiency identified, corrective actions completed',
    remediation_plan: 'Structural reinforcement, safety training, third-party inspection',
    remediation_progress: 95,
  },
  {
    case_id: 'case_005',
    title: 'Whistleblower - wage theft allegation',
    type: 'whistleblower',
    status: 'investigation',
    priority: 'high',
    supplier_id: 'sup_005',
    supplier_name: 'Assembly Services Co',
    impact_type: 'human_rights',
    description: 'Anonymous report claiming systematic underpayment of overtime wages',
    reported_at: '2025-01-28T11:00:00Z',
    due_date: '2025-03-01',
    assignee: 'Legal Team',
    evidence_count: 3,
    investigation_findings: null,
    remediation_plan: null,
    remediation_progress: 0,
  },
];

/**
 * Summary statistics card component
 */
const StatCard = ({ icon: Icon, label, value, trend, trendUp, color }) => (
  <Card className="bg-[#0F0F0F] border-[#1F1F1F]">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={18} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs ${trendUp ? 'text-red-400' : 'text-emerald-400'}`}>
            <TrendingUp size={12} className={trendUp ? '' : 'rotate-180'} />
            {trend}
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-[#6B6B6B] mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

/**
 * Case card component for list view
 */
const CaseCard = ({ caseData, onClick }) => {
  const status = CASE_STATUSES[caseData.status];
  const priority = CASE_PRIORITIES[caseData.priority];
  const type = CASE_TYPES[caseData.type];
  const StatusIcon = status.icon;
  const TypeIcon = type.icon;
  
  const dueDate = new Date(caseData.due_date);
  const isOverdue = dueDate < new Date() && caseData.status !== 'closed';
  const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
  
  return (
    <Card 
      className="bg-[#0F0F0F] border-[#1F1F1F] hover:border-[#2F2F2F] cursor-pointer transition-all"
      onClick={() => onClick(caseData)}
      data-testid={`case-card-${caseData.case_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${priority.color} border text-[10px]`}>
                {priority.label}
              </Badge>
              <Badge className={`${status.color} text-[10px]`}>
                <StatusIcon size={10} className="mr-1" />
                {status.label}
              </Badge>
              <Badge variant="outline" className="text-[10px] text-[#6B6B6B]">
                <TypeIcon size={10} className="mr-1" />
                {type.label}
              </Badge>
            </div>
            
            <h3 className="text-sm font-medium text-white truncate">
              {caseData.title}
            </h3>
            
            <p className="text-xs text-[#6B6B6B] mt-1 line-clamp-2">
              {caseData.description}
            </p>
            
            <div className="flex items-center gap-4 mt-3 text-xs text-[#6B6B6B]">
              <span className="flex items-center gap-1">
                <Users size={12} />
                {caseData.supplier_name}
              </span>
              <span className="flex items-center gap-1">
                <Link2 size={12} />
                {caseData.evidence_count} evidence
              </span>
              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                <Calendar size={12} />
                {isOverdue ? 'Overdue' : `${daysUntilDue}d remaining`}
              </span>
            </div>
            
            {caseData.remediation_progress > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#6B6B6B]">Remediation Progress</span>
                  <span className="text-white">{caseData.remediation_progress}%</span>
                </div>
                <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${caseData.remediation_progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <button className="p-1 text-[#6B6B6B] hover:text-white">
            <ChevronRight size={16} />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Main Cases & Remediation Page Component
 */
const CasesRemediation = () => {
  // State
  const [cases, setCases] = useState(MOCK_CASES);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  
  // Calculate statistics
  const stats = {
    total: cases.length,
    open: cases.filter(c => c.status !== 'closed').length,
    critical: cases.filter(c => c.priority === 'critical' && c.status !== 'closed').length,
    overdue: cases.filter(c => {
      const due = new Date(c.due_date);
      return due < new Date() && c.status !== 'closed';
    }).length,
  };
  
  // Filter cases
  const filteredCases = cases.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        c.title.toLowerCase().includes(query) ||
        c.supplier_name.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
      );
    }
    return true;
  });
  
  // Group cases by status for kanban view
  const casesByStatus = Object.keys(CASE_STATUSES).reduce((acc, status) => {
    acc[status] = filteredCases.filter(c => c.status === status);
    return acc;
  }, {});
  
  return (
    <div className="space-y-6" data-testid="cases-remediation-page">
      {/* Defensibility Flywheel - Horizontal Process Layer */}
      <DefensibilityFlywheel 
        currentStep="evidence"
        context="cases"
        entityId={selectedCase?.case_id}
      />
      
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Cases & Remediation</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage incidents, investigations, and remediation tracking
          </p>
        </div>
        <Button 
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="new-case-btn"
        >
          <Plus size={16} className="mr-2" />
          New Case
        </Button>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          icon={FileText}
          label="Total Cases"
          value={stats.total}
          color="bg-blue-500/20 text-blue-400"
        />
        <StatCard 
          icon={Clock}
          label="Open Cases"
          value={stats.open}
          trend="+3 this week"
          trendUp
          color="bg-amber-500/20 text-amber-400"
        />
        <StatCard 
          icon={AlertTriangle}
          label="Critical Priority"
          value={stats.critical}
          color="bg-red-500/20 text-red-400"
        />
        <StatCard 
          icon={Calendar}
          label="Overdue"
          value={stats.overdue}
          color="bg-orange-500/20 text-orange-400"
        />
      </div>
      
      {/* Filters and Search */}
      <Card className="bg-[#0F0F0F] border-[#1F1F1F]">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder-[#6B6B6B] focus:outline-none focus:border-emerald-500/50"
                data-testid="case-search-input"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6B6B6B]">Status:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    filter === 'all' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
                  }`}
                  data-testid="filter-all"
                >
                  All
                </button>
                {Object.entries(CASE_STATUSES).slice(0, 4).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      filter === key
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
                    }`}
                    data-testid={`filter-${key}`}
                  >
                    {val.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Cases List */}
      <div className="space-y-3">
        {filteredCases.length === 0 ? (
          <Card className="bg-[#0F0F0F] border-[#1F1F1F]">
            <CardContent className="p-8 text-center">
              <FileText size={32} className="mx-auto text-[#6B6B6B] mb-3" />
              <p className="text-sm text-[#6B6B6B]">No cases found matching your criteria</p>
            </CardContent>
          </Card>
        ) : (
          filteredCases.map(caseData => (
            <CaseCard 
              key={caseData.case_id}
              caseData={caseData}
              onClick={setSelectedCase}
            />
          ))
        )}
      </div>
      
      {/* Case Detail Drawer would go here */}
      {selectedCase && (
        <div className="fixed inset-y-0 right-0 w-[480px] bg-[#0A0A0A] border-l border-[#1F1F1F] z-50 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Case Details</h2>
              <button 
                onClick={() => setSelectedCase(null)}
                className="text-[#6B6B6B] hover:text-white"
                data-testid="close-case-drawer"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Badge className={`${CASE_PRIORITIES[selectedCase.priority].color} border`}>
                  {CASE_PRIORITIES[selectedCase.priority].label}
                </Badge>
              </div>
              
              <h3 className="text-white font-medium">{selectedCase.title}</h3>
              
              <p className="text-sm text-[#A0A0A0]">{selectedCase.description}</p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#6B6B6B]">Status</p>
                  <p className="text-white">{CASE_STATUSES[selectedCase.status].label}</p>
                </div>
                <div>
                  <p className="text-[#6B6B6B]">Type</p>
                  <p className="text-white">{CASE_TYPES[selectedCase.type].label}</p>
                </div>
                <div>
                  <p className="text-[#6B6B6B]">Supplier</p>
                  <p className="text-white">{selectedCase.supplier_name}</p>
                </div>
                <div>
                  <p className="text-[#6B6B6B]">Assignee</p>
                  <p className="text-white">{selectedCase.assignee || 'Unassigned'}</p>
                </div>
              </div>
              
              {selectedCase.investigation_findings && (
                <div>
                  <p className="text-[#6B6B6B] text-sm mb-1">Investigation Findings</p>
                  <p className="text-sm text-white bg-[#1F1F1F] p-3 rounded-lg">
                    {selectedCase.investigation_findings}
                  </p>
                </div>
              )}
              
              {selectedCase.remediation_plan && (
                <div>
                  <p className="text-[#6B6B6B] text-sm mb-1">Remediation Plan</p>
                  <p className="text-sm text-white bg-[#1F1F1F] p-3 rounded-lg">
                    {selectedCase.remediation_plan}
                  </p>
                </div>
              )}
              
              <div className="pt-4 border-t border-[#1F1F1F]">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  View Full Case
                  <ArrowUpRight size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CasesRemediation;
